import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import googleDriveService from '../features/integrations/services/googleDriveService';

/**
 * OAuth callback page for Google Drive integration
 * Handles the redirect from Google OAuth and directly calls backend
 */
export const GoogleDriveCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');

    if (error) {
      console.error('Google Drive OAuth error:', error);
      setStatus('error');
      setErrorMessage(error);

      // Redirect to integrations page after 3 seconds
      setTimeout(() => {
        navigate('/integrations?error=' + error);
      }, 3000);
      return;
    }

    if (code) {
      console.log('Google Drive OAuth successful, exchanging code...');

      // Directly call the backend API to connect
      googleDriveService.connectDrive(code)
        .then(() => {
          console.log('Drive connected successfully!');
          setStatus('success');

          // Close popup if opened in popup, otherwise redirect
          if (window.opener) {
            // Try to notify parent (may fail due to CORS, but connection is already done)
            try {
              window.opener.postMessage({
                type: 'GOOGLE_DRIVE_AUTH_SUCCESS'
              }, '*');
            } catch {
              console.log('Could not notify parent, but connection succeeded');
            }

            // Close after 1 second to show success message
            setTimeout(() => {
              window.close();
            }, 1000);
          } else {
            // Redirect to integrations page
            setTimeout(() => {
              navigate('/integrations?connected=true');
            }, 1000);
          }
        })
        .catch((err) => {
          console.error('Failed to connect Drive:', err);
          console.error('Error details:', {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status
          });

          const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to connect Google Drive';
          setStatus('error');
          setErrorMessage(errorMsg);

          // Redirect after 3 seconds
          setTimeout(() => {
            navigate('/integrations?error=' + encodeURIComponent(errorMsg));
          }, 3000);
        });
    }
  }, [searchParams, navigate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ p: 3 }}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ mt: 3 }}>
            Connecting your Google Drive...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait...
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            Google Drive connected successfully!
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {window.opener ? 'This window will close automatically...' : 'Redirecting...'}
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage || 'Failed to connect Google Drive'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Redirecting back to integrations page...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default GoogleDriveCallbackPage;
